/**
 * Image Display upload helpers.
 *
 * The hosted UI PUTs processed image bytes to a GCS V4 signed URL. Electron's
 * renderer fetch() is a common failure point for that step (data: URL fetch is
 * flaky in a sandboxed renderer, and Chromium may mutate Content-Type in a way
 * that invalidates the signed URL). Prefer a Blob built without fetch, and
 * when running inside Signal Bridge Desktop, upload via the main process.
 */

export function dataUrlToBlob(dataUrl) {
  const raw = String(dataUrl || '').trim();
  const match = raw.match(/^data:([^;]+);base64,([A-Za-z0-9+/=\s]+)$/i);
  if (!match) {
    throw new Error('Invalid image data URL');
  }

  const contentType = match[1] || 'image/png';
  const binary = atob(match[2].replace(/\s+/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

function bytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function blobToArrayBuffer(blob) {
  if (typeof FileReader === 'function') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Failed to read image bytes'));
      reader.readAsArrayBuffer(blob);
    });
  }
  if (blob && typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }
  return new Response(blob).arrayBuffer();
}

async function putViaElectron({ uploadUrl, headers, blob }) {
  const api = typeof window !== 'undefined' ? window.electronAPI?.putSignedUrl : null;
  if (typeof api !== 'function') return null;

  const buffer = await blobToArrayBuffer(blob);
  const result = await api({
    uploadUrl,
    headers,
    bodyBase64: bytesToBase64(new Uint8Array(buffer)),
  });
  return result;
}

export async function putImageToSignedUrl({ uploadUrl, headers, dataUrl, blob, contentType }) {
  if (!uploadUrl) throw new Error('Upload URL is required');

  const bodyBlob = blob || dataUrlToBlob(dataUrl);
  const resolvedHeaders = headers || {
    'Content-Type': contentType || bodyBlob.type || 'image/png',
  };

  const electronResult = await putViaElectron({
    uploadUrl,
    headers: resolvedHeaders,
    blob: bodyBlob,
  });
  if (electronResult) {
    if (!electronResult.ok) {
      const detail = electronResult.bodyPreview
        ? ` ${String(electronResult.bodyPreview).slice(0, 180)}`
        : '';
      throw new Error(`Image upload failed (${electronResult.status})${detail}`);
    }
    return { via: 'electron' };
  }

  const putResp = await fetch(uploadUrl, {
    method: 'PUT',
    headers: resolvedHeaders,
    body: bodyBlob,
  });
  if (!putResp.ok) {
    throw new Error(`Image upload failed (${putResp.status})`);
  }
  return { via: 'fetch' };
}
